import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { AlertTriangle, ShieldCheck, RefreshCw, Power, Users, History, ChevronLeft, ChevronRight } from 'lucide-react';
import { killSwitchService } from '../services/api';
import { ROLES } from '../utils/rbac';

/**
 * System Control (kill switch) panel — SUPERADMIN only.
 *
 * Independently pauses the Booking Engine and/or the PMS. Activating a
 * surface emails every privileged user (staff, admins, accounts, inventory,
 * service) about the temporary closure; restoring sends the all-clear.
 */

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return `${new Date(value).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: 'Asia/Kolkata',
    })} IST`;
  } catch {
    return '—';
  }
};

/** `datetime-local` needs a local `YYYY-MM-DDTHH:mm` string, not an ISO UTC one. */
const toLocalInputValue = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const HISTORY_PAGE_SIZE = 10;

const card = {
  backgroundColor: '#ffffff',
  border: '1px solid #E3DFD3',
  borderRadius: '12px',
  padding: '24px',
  marginBottom: '20px',
};

const label = {
  display: 'block',
  fontSize: '13px',
  fontWeight: 700,
  color: '#36433B',
  marginBottom: '6px',
};

const input = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #D2CDBF',
  borderRadius: '8px',
  fontSize: '14px',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

function SystemControl({ user }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState(null);

  const [bookingEngineDisabled, setBookingEngineDisabled] = useState(false);
  const [pmsDisabled, setPmsDisabled] = useState(false);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [reason, setReason] = useState('');
  const [expectedRestoreAt, setExpectedRestoreAt] = useState('');
  const [notifyStaff, setNotifyStaff] = useState(true);

  const [history, setHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPagination, setHistoryPagination] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const isSuperAdmin = user?.role === ROLES.SUPERADMIN;

  const applyState = useCallback((data) => {
    // Merge, because the update/restore responses omit the read-only extras
    // (defaults, notifiableUserCount) that only the GET endpoint returns.
    setState((prev) => ({ ...(prev || {}), ...data }));
    setBookingEngineDisabled(!!data.bookingEngineDisabled);
    setPmsDisabled(!!data.pmsDisabled);
    setTitle(data.title || '');
    setMessage(data.message || '');
    setReason(data.reason || '');
    setExpectedRestoreAt(toLocalInputValue(data.expectedRestoreAt));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await killSwitchService.get();
      if (response?.data) applyState(response.data);
    } catch (error) {
      console.error('Failed to load kill switch state:', error);
      toast.error(error?.error || 'Could not load system control state');
    } finally {
      setLoading(false);
    }
  }, [applyState]);

  const loadHistory = useCallback(async (page = 1) => {
    setHistoryLoading(true);
    try {
      const response = await killSwitchService.history({ page, limit: HISTORY_PAGE_SIZE });
      setHistory(Array.isArray(response?.data) ? response.data : []);
      setHistoryPagination(response?.pagination || null);
      setHistoryPage(page);
    } catch (error) {
      console.error('Failed to load kill switch history:', error);
      toast.error(error?.error || 'Could not load the kill switch audit trail');
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) {
      load();
      loadHistory(1);
    } else {
      setLoading(false);
    }
  }, [isSuperAdmin, load, loadHistory]);

  if (!isSuperAdmin) {
    return (
      <div style={{ ...card, textAlign: 'center', color: '#5F6B61' }}>
        System Control is restricted to Super Admins.
      </div>
    );
  }

  const currentlyDown = !!state && (state.bookingEngineDisabled || state.pmsDisabled);
  const willBeDown = bookingEngineDisabled || pmsDisabled;
  const dirty =
    !!state &&
    (bookingEngineDisabled !== state.bookingEngineDisabled ||
      pmsDisabled !== state.pmsDisabled ||
      title !== (state.title || '') ||
      message !== (state.message || '') ||
      reason !== (state.reason || '') ||
      expectedRestoreAt !== toLocalInputValue(state.expectedRestoreAt));

  const submit = async () => {
    const surfaces = [
      bookingEngineDisabled && !state?.bookingEngineDisabled ? 'Booking Engine' : null,
      pmsDisabled && !state?.pmsDisabled ? 'PMS' : null,
    ].filter(Boolean);

    if (surfaces.length > 0) {
      const confirmed = window.confirm(
        `Pause ${surfaces.join(' and ')} for every user except Super Admins?\n\n` +
          (notifyStaff
            ? `All ${state?.notifiableUserCount ?? 'privileged'} staff/admin accounts will be emailed about the temporary closure.`
            : 'Staff will NOT be emailed.')
      );
      if (!confirmed) return;
    }

    setSaving(true);
    try {
      const response = await killSwitchService.update({
        bookingEngineDisabled,
        pmsDisabled,
        title: title.trim() || null,
        message: message.trim() || null,
        reason: reason.trim() || null,
        // datetime-local yields local wall-clock time; send it as a real instant
        expectedRestoreAt: expectedRestoreAt ? new Date(expectedRestoreAt).toISOString() : null,
        notifyStaff,
      });
      if (response?.data) applyState(response.data);
      toast.success(response?.message || 'System control updated');
      loadHistory(1);
    } catch (error) {
      console.error('Failed to update kill switch:', error);
      toast.error(error?.error || error?.message || 'Failed to update system control');
    } finally {
      setSaving(false);
    }
  };

  const restoreAll = async () => {
    if (!window.confirm('Bring the Booking Engine and PMS back online for everyone?')) return;
    setSaving(true);
    try {
      const response = await killSwitchService.restore(notifyStaff);
      if (response?.data) applyState(response.data);
      toast.success(response?.message || 'All services restored');
      loadHistory(1);
    } catch (error) {
      console.error('Failed to restore services:', error);
      toast.error(error?.error || error?.message || 'Failed to restore services');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ ...card, textAlign: 'center', color: '#5F6B61' }}>
        <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }} />
        <div style={{ marginTop: '8px' }}>Loading system control…</div>
        <style>{'@keyframes spin { from { transform: rotate(0deg);} to { transform: rotate(360deg);} }'}</style>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '860px' }}>
      {/* Live status */}
      <div
        style={{
          ...card,
          backgroundColor: currentlyDown ? '#F9EEEB' : '#F0F3EE',
          borderColor: currentlyDown ? '#E5B3A9' : '#C4D4C8',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          {currentlyDown ? (
            <AlertTriangle size={24} style={{ color: '#772D21' }} />
          ) : (
            <ShieldCheck size={24} style={{ color: '#122E20' }} />
          )}
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1A3E2A' }}>
              {currentlyDown ? 'Kill switch is ACTIVE' : 'All systems operational'}
            </h2>
            <div style={{ fontSize: '13px', color: '#5F6B61', marginTop: '2px' }}>
              {currentlyDown
                ? 'Only Super Admins can use the paused services right now.'
                : 'Booking Engine and PMS are live for all users.'}
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px' }}>
          <StatusPill
            name="Booking Engine"
            down={state?.bookingEngineDisabled}
            since={state?.bookingEngineDisabledAt}
          />
          <StatusPill name="PMS" down={state?.pmsDisabled} since={state?.pmsDisabledAt} />
        </div>

        {state?.updatedByEmail && (
          <div style={{ fontSize: '12px', color: '#5F6B61', marginTop: '16px' }}>
            Last changed by {state.updatedByName || state.updatedByEmail} ({state.updatedByEmail}) ·{' '}
            {formatDateTime(state.updatedAt)}
            {state.lastNotifiedAt
              ? ` · ${state.lastNotifiedCount} staff notified at ${formatDateTime(state.lastNotifiedAt)}`
              : ''}
          </div>
        )}
      </div>

      {/* Controls */}
      <div style={card}>
        <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: 700, color: '#1A3E2A' }}>
          Pause services
        </h3>
        <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: '#5F6B61' }}>
          Tick a service to take it offline. Each one can be paused and restored independently. Your Super Admin
          session always stays fully functional.
        </p>

        <Checkbox
          checked={bookingEngineDisabled}
          onChange={setBookingEngineDisabled}
          title="Turn off the Booking Engine"
          description="Guests see a maintenance page with your contact details instead of the public booking site. All unauthenticated API traffic is paused."
        />

        <Checkbox
          checked={pmsDisabled}
          onChange={setPmsDisabled}
          title="Turn off the PMS"
          description="Staff, property admins, accounts, inventory and service accounts are signed out of the dashboard and see the 'system temporarily down' page. Their API access is paused."
        />

        <div style={{ height: '1px', backgroundColor: '#E3DFD3', margin: '20px 0' }} />

        <div style={{ marginBottom: '16px' }}>
          <label style={label} htmlFor="ks-title">Headline shown to users</label>
          <input
            id="ks-title"
            style={input}
            value={title}
            maxLength={160}
            placeholder={state?.defaults?.title || 'System temporarily unavailable'}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={label} htmlFor="ks-message">Message shown to users</label>
          <textarea
            id="ks-message"
            style={{ ...input, minHeight: '90px', resize: 'vertical' }}
            value={message}
            maxLength={2000}
            placeholder={state?.defaults?.message || ''}
            onChange={(e) => setMessage(e.target.value)}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px' }}>
          <div>
            <label style={label} htmlFor="ks-reason">Internal reason (included in the staff email)</label>
            <input
              id="ks-reason"
              style={input}
              value={reason}
              maxLength={1000}
              placeholder="e.g. Database migration"
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
          <div>
            <label style={label} htmlFor="ks-restore">Expected restoration (optional)</label>
            <input
              id="ks-restore"
              type="datetime-local"
              style={input}
              value={expectedRestoreAt}
              onChange={(e) => setExpectedRestoreAt(e.target.value)}
            />
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            marginTop: '20px',
            padding: '14px',
            backgroundColor: '#F5F3EB',
            border: '1px solid #E3DFD3',
            borderRadius: '8px',
          }}
        >
          <input
            id="ks-notify"
            type="checkbox"
            checked={notifyStaff}
            onChange={(e) => setNotifyStaff(e.target.checked)}
            style={{ width: '18px', height: '18px', marginTop: '1px', cursor: 'pointer' }}
          />
          <label htmlFor="ks-notify" style={{ cursor: 'pointer' }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#1A3E2A', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Users size={15} /> Email all privileged users
            </div>
            <div style={{ fontSize: '12px', color: '#5F6B61', marginTop: '2px' }}>
              {typeof state?.notifiableUserCount === 'number'
                ? `${state.notifiableUserCount} accounts (staff, property admins, accounts, inventory, service, super admins) `
                : 'All staff and admin accounts '}
              are notified when a service is paused, and again when it is restored.
            </div>
          </label>
        </div>

        {willBeDown && (
          <div
            style={{
              marginTop: '18px',
              padding: '14px',
              backgroundColor: '#F3E6CB',
              border: '1px solid #D8B26A',
              borderRadius: '8px',
              fontSize: '13px',
              color: '#684A1F',
            }}
          >
            <strong>Heads up:</strong> Razorpay webhooks, login and this control panel stay reachable while services
            are paused, so payments already in flight still reconcile and you can always switch everything back on.
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', marginTop: '24px', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={submit}
            disabled={saving || !dirty}
            style={{
              padding: '12px 22px',
              backgroundColor: saving || !dirty ? '#A5A89B' : willBeDown ? '#772D21' : '#122E20',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 700,
              cursor: saving || !dirty ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Power size={16} />
            {saving ? 'Applying…' : willBeDown ? 'Apply & pause services' : 'Apply changes'}
          </button>

          <button
            type="button"
            onClick={restoreAll}
            disabled={saving || !currentlyDown}
            style={{
              padding: '12px 22px',
              backgroundColor: saving || !currentlyDown ? '#E3DFD3' : '#122E20',
              color: saving || !currentlyDown ? '#A5A89B' : '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 700,
              cursor: saving || !currentlyDown ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <ShieldCheck size={16} />
            Restore everything
          </button>

          <button
            type="button"
            onClick={() => {
              load();
              loadHistory(historyPage);
            }}
            disabled={saving}
            style={{
              padding: '12px 18px',
              backgroundColor: '#EEEBE1',
              color: '#36433B',
              border: '1px solid #D2CDBF',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Audit trail — super admins only, both here and on the endpoint */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '4px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#1A3E2A', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <History size={17} /> Kill switch audit trail
          </h3>
          <button
            type="button"
            onClick={() => loadHistory(historyPage)}
            disabled={historyLoading}
            style={{
              padding: '7px 12px',
              backgroundColor: '#EEEBE1',
              color: '#36433B',
              border: '1px solid #D2CDBF',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 600,
              cursor: historyLoading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <RefreshCw size={13} style={{ animation: historyLoading ? 'spin 1s linear infinite' : 'none' }} />
            Refresh
          </button>
        </div>
        <p style={{ margin: '0 0 18px 0', fontSize: '13px', color: '#5F6B61' }}>
          Every activation, change and restoration — who did it and when. Visible to Super Admins only.
        </p>

        <KillSwitchHistory
          entries={history}
          loading={historyLoading}
          page={historyPage}
          pagination={historyPagination}
          onPageChange={loadHistory}
        />
      </div>

      <style>{'@keyframes spin { from { transform: rotate(0deg);} to { transform: rotate(360deg);} }'}</style>
    </div>
  );
}

/** One row per audit entry, newest first. */
function KillSwitchHistory({ entries, loading, page, pagination, onPageChange }) {
  if (loading && entries.length === 0) {
    return (
      <div style={{ padding: '28px', textAlign: 'center', color: '#5F6B61', fontSize: '14px' }}>
        Loading audit trail…
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div
        style={{
          padding: '28px',
          textAlign: 'center',
          color: '#5F6B61',
          fontSize: '14px',
          backgroundColor: '#F5F3EB',
          border: '1px dashed #D2CDBF',
          borderRadius: '8px',
        }}
      >
        The kill switch has never been used.
      </div>
    );
  }

  const totalPages = pagination?.totalPages || 1;

  return (
    <>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '620px' }}>
          <thead>
            <tr style={{ backgroundColor: '#F5F3EB' }}>
              <Th>When</Th>
              <Th>Action</Th>
              <Th>Who</Th>
              <Th>Services affected</Th>
              <Th>Reason</Th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.id} style={{ borderBottom: '1px solid #EEEBE1' }}>
                <Td>
                  <div style={{ fontWeight: 600, color: '#1A3E2A', whiteSpace: 'nowrap' }}>
                    {formatDateTime(entry.createdAt)}
                  </div>
                </Td>
                <Td>
                  <ActionBadge action={entry.action} />
                </Td>
                <Td>
                  <div style={{ fontWeight: 600, color: '#1A3E2A' }}>
                    {entry.actor?.name || entry.actor?.email || 'Unknown user'}
                  </div>
                  {entry.actor?.email && (
                    <div style={{ fontSize: '12px', color: '#5F6B61' }}>{entry.actor.email}</div>
                  )}
                </Td>
                <Td>
                  <SurfaceSummary entry={entry} />
                </Td>
                <Td>
                  <div style={{ color: entry.reason ? '#36433B' : '#A5A89B', maxWidth: '260px' }}>
                    {entry.reason || '—'}
                  </div>
                  {entry.notifyStaff === false && (
                    <div style={{ fontSize: '11px', color: '#825D26', marginTop: '3px', fontWeight: 600 }}>
                      Staff not notified
                    </div>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            marginTop: '16px',
          }}
        >
          <span style={{ fontSize: '12px', color: '#5F6B61' }}>
            Page {page} of {totalPages} · {pagination?.totalItems ?? entries.length} entries
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <PagerButton
              disabled={loading || page <= 1}
              onClick={() => onPageChange(page - 1)}
              icon={ChevronLeft}
              label="Previous"
            />
            <PagerButton
              disabled={loading || page >= totalPages}
              onClick={() => onPageChange(page + 1)}
              icon={ChevronRight}
              label="Next"
            />
          </div>
        </div>
      )}
    </>
  );
}

const ACTION_STYLES = {
  KILL_SWITCH_ACTIVATED: { label: 'Activated', bg: '#F2D9D3', color: '#772D21' },
  KILL_SWITCH_UPDATED: { label: 'Updated', bg: '#F3E6CB', color: '#684A1F' },
  KILL_SWITCH_DEACTIVATED: { label: 'Restored', bg: '#E1E9E2', color: '#122E20' },
};

function ActionBadge({ action }) {
  const style = ACTION_STYLES[action] || { label: action, bg: '#EEEBE1', color: '#4A564D' };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '3px 10px',
        borderRadius: '999px',
        fontSize: '11px',
        fontWeight: 700,
        letterSpacing: '0.03em',
        backgroundColor: style.bg,
        color: style.color,
        whiteSpace: 'nowrap',
      }}
    >
      {style.label}
    </span>
  );
}

const SURFACE_LABELS = { BOOKING_ENGINE: 'Booking Engine', PMS: 'PMS' };

function SurfaceSummary({ entry }) {
  const paused = (entry.newlyDisabled || []).map((s) => SURFACE_LABELS[s] || s);
  const restored = (entry.newlyEnabled || []).map((s) => SURFACE_LABELS[s] || s);

  if (paused.length === 0 && restored.length === 0) {
    // Message/reason-only edit — say what was down at the time instead.
    const down = [
      entry.bookingEngineDisabled ? 'Booking Engine' : null,
      entry.pmsDisabled ? 'PMS' : null,
    ].filter(Boolean);
    return (
      <span style={{ color: '#5F6B61' }}>
        {down.length ? `Notice updated (${down.join(' & ')} paused)` : 'Notice updated'}
      </span>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
      {paused.length > 0 && (
        <span style={{ color: '#772D21', fontWeight: 600 }}>Paused: {paused.join(', ')}</span>
      )}
      {restored.length > 0 && (
        <span style={{ color: '#122E20', fontWeight: 600 }}>▶ Restored: {restored.join(', ')}</span>
      )}
    </div>
  );
}

function PagerButton({ disabled, onClick, icon: Icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '6px 12px',
        backgroundColor: disabled ? '#F5F3EB' : '#ffffff',
        color: disabled ? '#A5A89B' : '#36433B',
        border: '1px solid #D2CDBF',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
      }}
    >
      <Icon size={14} />
      {label}
    </button>
  );
}

function Th({ children }) {
  return (
    <th
      style={{
        textAlign: 'left',
        padding: '10px 12px',
        fontSize: '11px',
        fontWeight: 700,
        letterSpacing: '0.04em',
        color: '#5F6B61',
        textTransform: 'uppercase',
        borderBottom: '1px solid #E3DFD3',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </th>
  );
}

function Td({ children }) {
  return <td style={{ padding: '12px', verticalAlign: 'top', color: '#36433B' }}>{children}</td>;
}

function StatusPill({ name, down, since }) {
  return (
    <div
      style={{
        padding: '14px 16px',
        backgroundColor: '#ffffff',
        border: `1px solid ${down ? '#E5B3A9' : '#C4D4C8'}`,
        borderRadius: '10px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
        <span style={{ fontSize: '14px', fontWeight: 700, color: '#1A3E2A' }}>{name}</span>
        <span
          style={{
            padding: '3px 10px',
            borderRadius: '999px',
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.04em',
            backgroundColor: down ? '#F2D9D3' : '#E1E9E2',
            color: down ? '#772D21' : '#122E20',
          }}
        >
          {down ? 'PAUSED' : 'LIVE'}
        </span>
      </div>
      {down && since && (
        <div style={{ fontSize: '12px', color: '#5F6B61', marginTop: '6px' }}>Since {formatDateTime(since)}</div>
      )}
    </div>
  );
}

function Checkbox({ checked, onChange, title, description }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '16px',
        border: `1px solid ${checked ? '#D48A7C' : '#E3DFD3'}`,
        backgroundColor: checked ? '#F9EEEB' : '#ffffff',
        borderRadius: '10px',
        marginBottom: '12px',
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ width: '20px', height: '20px', marginTop: '2px', cursor: 'pointer', flexShrink: 0 }}
      />
      <div onClick={() => onChange(!checked)} style={{ cursor: 'pointer' }}>
        <div style={{ fontSize: '15px', fontWeight: 700, color: '#1A3E2A' }}>{title}</div>
        <div style={{ fontSize: '13px', color: '#5F6B61', marginTop: '4px', lineHeight: '1.5' }}>{description}</div>
      </div>
    </div>
  );
}

export default SystemControl;
